"use client"

import Image from "next/image"
import { useTranslations } from "next-intl"
import { Smartphone, Shield, Zap, type LucideIcon } from "lucide-react"
import { Link } from "@/i18n/navigation"
import { AnimatedSection } from "../animated-section"
import { STORE_URLS, resolveInstallTarget } from "@/lib/install-targets"
import { useBrowser, useDevice } from "@/hooks/use-browser"
import { cn } from "@/lib/utils"

/** Feature list — copy lives in `start.appPromotion.features.<key>`; icon stays in code. */
const FEATURES: { key: string; icon: LucideIcon }[] = [
  { key: "donationReminder", icon: Zap },
  { key: "privacy", icon: Shield },
  { key: "setup", icon: Smartphone },
]

/* ── Brand-color icons ─────────────────────────────────────────────── */

function AppleColorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 17 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M14.07 10.58c-.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.62-1.7-3.18-1.72-1.35-.14-2.65.8-3.34.8-.69 0-1.76-.78-2.89-.76a4.27 4.27 0 0 0-3.6 2.2c-1.54 2.67-.39 6.62 1.1 8.79.74 1.06 1.61 2.25 2.76 2.2 1.1-.04 1.52-.72 2.86-.72 1.33 0 1.72.72 2.88.7 1.2-.02 1.95-1.08 2.67-2.14.85-1.23 1.19-2.41 1.21-2.48-.03-.01-2.32-.89-2.35-3.56zM11.82 3.78A3.88 3.88 0 0 0 12.72.65a3.95 3.95 0 0 0-2.56 1.32 3.7 3.7 0 0 0-.92 2.68 3.27 3.27 0 0 0 2.58-0.87z" />
    </svg>
  )
}

function GooglePlayColorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.22.56a1.57 1.57 0 0 0-.57 1.28v18.32c0 .52.22.97.57 1.28l.07.06L12.08 11v-.13L1.29.5l-.07.06z" fill="#4285F4" />
      <path d="M15.68 14.6l-3.6-3.6v-.13l3.6-3.6.08.05 4.27 2.43c1.22.69 1.22 1.83 0 2.52l-4.27 2.43-.08.05v-.15z" fill="#FBBC04" />
      <path d="M15.76 14.45L12.08 11 1.22 21.44c.4.43 1.07.48 1.82.01l12.72-7z" fill="#EA4335" />
      <path d="M15.76 7.42L3.04.56C2.29.09 1.62.14 1.22.57L12.08 11l3.68-3.58z" fill="#34A853" />
    </svg>
  )
}

function ChromeColorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="cg-a" x1="3.22" y1="15" x2="44.78" y2="15" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#d93025" /><stop offset="1" stopColor="#ea4335" />
        </linearGradient>
        <linearGradient id="cg-b" x1="20.72" y1="47.68" x2="41.5" y2="11.68" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#fcc934" /><stop offset="1" stopColor="#fbbc04" />
        </linearGradient>
        <linearGradient id="cg-c" x1="26.6" y1="46.5" x2="5.82" y2="10.51" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#1e8e3e" /><stop offset="1" stopColor="#34a853" />
        </linearGradient>
      </defs>
      <circle cx="24" cy="24" r="12" fill="#fff" />
      <path d="M24 12h20.78a24 24 0 0 0-41.56 0L13.61 30A12 12 0 0 1 24 12z" fill="url(#cg-a)" />
      <circle cx="24" cy="24" r="9.5" fill="#1a73e8" />
      <path d="M34.39 30L24 48a24 24 0 0 0 20.78-36H24a12 12 0 0 1 10.39 18z" fill="url(#cg-b)" />
      <path d="M13.61 30L3.22 12a24 24 0 0 0 20.78 36L34.39 30a12 12 0 0 1-20.78 0z" fill="url(#cg-c)" />
    </svg>
  )
}

/* ── Badge button ──────────────────────────────────────────────────── */

function StoreBadgeButton({
  href,
  prefix,
  label,
  icon,
  className,
}: {
  href: string
  prefix: string
  label: string
  icon: React.ReactNode
  className?: string
}) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "flex w-full items-center justify-center gap-2.5 rounded-xl border border-primary/30 bg-primary/5 px-4 py-2.5 backdrop-blur-md transition-colors hover:border-primary/50 hover:bg-primary/15 sm:w-auto sm:justify-start",
        className,
      )}
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center">
        {icon}
      </span>
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] text-foreground/60">{prefix}</span>
        <span className="text-sm font-semibold text-foreground">{label}</span>
      </span>
    </Link>
  )
}

/* ── Badge rows ────────────────────────────────────────────────────── */

function StoreBadgesColor() {
  const t = useTranslations("start.appPromotion")
  return (
    <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-center">
      <StoreBadgeButton
        href={STORE_URLS.appStore}
        prefix={t("badges.appStorePrefix")}
        label={t("badges.appStoreLabel")}
        icon={<AppleColorIcon className="h-6 w-6" />}
      />
      <StoreBadgeButton
        href={STORE_URLS.googlePlay}
        prefix={t("badges.googlePlayPrefix")}
        label={t("badges.googlePlayLabel")}
        icon={<GooglePlayColorIcon className="h-5 w-5" />}
      />
      <StoreBadgeButton
        href={STORE_URLS.chromeWebStore}
        prefix={t("badges.chromePrefix")}
        label={t("badges.chromeLabel")}
        icon={<ChromeColorIcon className="h-6 w-6" />}
        className="col-span-2 sm:col-span-1"
      />
    </div>
  )
}

/* ── Shared components ─────────────────────────────────────────────── */

function CombinedMockImage({ className }: { className?: string }) {
  const t = useTranslations("start.appPromotion")
  const device = useDevice()
  const browser = useBrowser() as "chrome" | "firefox" | "safari"
  const { url } = resolveInstallTarget({ device, browser })
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t("installCta")}
      className="block transition-transform hover:scale-[1.02]"
    >
      <Image
        src="/start/app-extension-mock.png"
        alt={t("mockAlt")}
        width={1200}
        height={834}
        className={className ?? "h-auto w-full max-w-[700px] drop-shadow-2xl"}
      />
    </a>
  )
}

function FeatureList() {
  const t = useTranslations("start.appPromotion")
  return (
    <div className="space-y-4">
      {FEATURES.map(({ key, icon: Icon }) => (
        <div key={key} className="flex items-start gap-3">
          <div className="shrink-0 rounded-full bg-primary/10 p-2">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="mb-0.5 font-semibold">{t(`features.${key}.title`)}</h3>
            <p className="text-sm text-muted-foreground">{t(`features.${key}.description`)}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

/* ── Variant ───────────────────────────────────────────────────────── */

/** combinedSplit — content left, mock image right, color badges. */
export function AppPromoCombinedSplit() {
  const t = useTranslations("start.appPromotion")
  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <AnimatedSection>
            <div>
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">{t("title")}</h2>
              <p className="mb-8 text-lg text-muted-foreground">{t("subtitle")}</p>
              <div className="mb-8">
                <FeatureList />
              </div>
              <StoreBadgesColor />
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <div className="flex items-center justify-center">
              <CombinedMockImage />
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  )
}
